import React from 'preact/compat'
import Api from '../api'
import { captureFetchException, loadStylesheet, sgVersion, showModal } from '../common/helpers'
import { NotificationPermissionRequestType, promptNotificationsPermission } from '../common/cloud-messaging'
import SVG from './SVG'
import Modal from './Modal'
import styles from '../../css/game-watch-modal.pcss.json'
import buttonsCss from '../../css/buttons.pcss.json'
import renderToString from 'preact-render-to-string'
import type { ComponentType } from 'preact'
import type { ReviewEditorProps } from './ReviewEditor/ReviewEditor'
import GameAddToCompilation from './GameAddToCompilation'
import { showToast } from './toast'
import cn from 'classnames'

declare global {
  // noinspection JSUnusedGlobalSymbols
  interface Window {
    gameStatus: { [key: number]: number }
  }
}

interface PropTypes {
  onClose: () => void,
  gameId: string,
  iconSet?: string,
  element?: HTMLElement,
  buttonClasses?: {
    blank: string,
    wishlist: string,
    playing: string,
    beaten: string,
    sleep: string
  }
}

interface StateTypes {
  gameName: string,
  status: number,
  notify: number,
  rating: number,
  gameIsReleased: boolean,
  loaded: boolean,
  releaseDate: string,
  favourite: boolean
}

export default class GameStatusDialog extends React.Component<PropTypes, StateTypes> {
  public state: StateTypes = {
    gameName: '',
    status: -1,
    notify: -1,
    rating: 0,
    gameIsReleased: false,
    loaded: false,
    favourite: false,
    releaseDate: 'Дата выхода неизвестна',
  }

  initialStatus = null

  protected statuses: {
    [key: number]: {
      svg: string,
      text: string
    }
  } = {
    0: {
      svg: 'wishlist',
      text: 'Буду играть'
    },
    1: {
      svg: 'playing',
      text: 'Играю'
    },
    2: {
      svg: 'beaten',
      text: 'Пройдено'
    },
    3: {
      svg: 'sleep',
      text: 'Заброшено'
    }
  }

  protected watchGame = async (status: number) => {
    if (status > 0 && !this.state.gameIsReleased) {
      showToast({
        type: 'error',
        text: 'Игра ещё не вышла и может быть добавлена только в «Буду играть»'
      })
      return
    }

    try {
      let result = await Api.post({
        url: `favourite/watch-game`,
        body: {
          gameId: this.props.gameId,
          status
        }
      })

      if (!result.success) {
        showToast({
          type: 'error',
          text: result.message,
        })
        return false
      }

      showToast({
        type: 'success',
        text: result.message,
      })

      if (result.status >= 0) {
        if (this.state.status === -1) {
          try {
            window.ym(36906115, 'reachGoal', 'game-add-to-library')
          } catch {
            // do nothing
          }
        }
        const statusInfo = this.statuses[result.status]
        this.setStatus(statusInfo, result.notify)
        window.dispatchEvent(new CustomEvent('game-status-changed', {
          detail: {
            gameId: this.props.gameId,
            status: result.status,
          }
        }))
        if (!window.gameStatus) {
          window.gameStatus = {}
        }
        window.gameStatus[parseInt(this.props.gameId)] = result.status
      } else {
        this.clearStatus()
      }

      this.setState({
        status: result.status,
        notify: result.notify,
      })
    } catch (error) {
      captureFetchException(error)
      showToast({
        type: 'error',
        text: 'При изменении статуса игры произошла ошибка',
      })
    }
  }

  public componentWillMount() {
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('game-watch-modal')
  }

  public componentDidMount() {
    window.addEventListener('changeGameNotify', this.setNotify)
    Api.get({ url: `games/fav-info/${this.props.gameId}` })
      .then(result => {
        this.initialStatus = result.status
        this.setState({
          gameName: result.gameName,
          status: result.status,
          notify: result.notify,
          rating: result.rating,
          gameIsReleased: result.gameIsReleased,
          loaded: true,
          releaseDate: result.releaseDate,
          favourite: result.favourite
        })
      })
      .catch((e) => {
        let text = 'Неизвестная ошибка'
        if (e.status && e.status === 404) {
          text = 'Игра не найдена'
        }
        showToast({
          type: 'error',
          text
        })
        this.props.onClose()
      })
  }

  public componentWillUnmount() {
    window.removeEventListener('changeGameNotify', this.setNotify)
    if (this.initialStatus !== this.state.status) {
      // noinspection JSIgnoredPromiseFromCall
      promptNotificationsPermission(NotificationPermissionRequestType.game_subscription)
    }
  }

  setRating = (rating: number) => {
    if (!this.state.gameIsReleased) {
      showToast({
        type: 'error',
        text: 'Игра ещё не вышла!'
      })
      return
    }

    const prevRating = this.state.rating
    this.setState({
      rating
    })

    Api.post<{
      type: 'success' | 'error',
      text: string,
      new_rate: number,
      rate_color: string,
      rate_count_string: string,
      user_rate: number
    }>({
      url: `games/rate`,
      body: {
        game_id: this.props.gameId,
        rate: rating,
      }
    })
      .then(result => {
        if (result.type !== 'success') {
          showToast({
            type: 'error',
            text: result.text,
          })
          this.setState({
            rating: prevRating
          })
          return
        }

        showToast({
          type: 'success',
          text: result.text,
        })
      })
      .catch((error) => {
        showToast({
          type: 'error',
          text: 'Произошла ошибка. Попробуй позже.',
        })
        this.setState({
          rating: prevRating
        })
        captureFetchException(error)
      })
  }

  showReviewForm = async () => {
    if (!this.state.gameIsReleased) {
      showToast({
        type: 'error',
        text: 'Игра ещё не вышла!'
      })
      return
    }
    this.props.onClose()
    const { default: ReviewEditor } = await import(`/dist/js/components/ReviewEditor/ReviewEditor.js?${sgVersion()}`)
    showModal(ReviewEditor as ComponentType<ReviewEditorProps>, {
      gameId: this.props.gameId
    })
  }

  setNotify = (e: CustomEvent<{
    notify: number
  }>) => {
    this.setState({
      notify: e.detail.notify
    }, () => {
      this.setStatus(this.statuses[this.state.status], e.detail.notify)
    })
  }

  setStatus = (status: {
    svg: string,
    text: string
  }, notify: number) => {
    if (!this.props.element) {
      return
    }
    const icon = this.props.element.querySelector('[data-game-watch-icon]')
    const text = this.props.element.querySelector('[data-game-watch-text]')
    if (text) {
      text.innerHTML = status.text
    }
    if (icon) {
      let iconSet = this.props.iconSet || 'color'
      let iconUrl = `game-status/${iconSet}/${status.svg}`
      if (iconSet.startsWith('/')) {
        iconUrl = `${iconSet.replace(/^\//, '')}/${status.svg}`
      }
      icon.outerHTML = renderToString(<SVG i={iconUrl} data-game-watch-icon data-game-watch-icon-status={status.svg}/>)
    }
    if (this.props.buttonClasses) {
      const buttonClass = this.props.buttonClasses[status.svg]
      if (buttonClass) {
        this.props.element.classList.remove(...Object.values(this.props.buttonClasses))
        this.props.element.classList.add(buttonClass)
      }
    }

    const notifyEl = this.props.element.parentElement.querySelector<HTMLButtonElement>('[data-toggle-game-notify]')
    if (notifyEl) {
      notifyEl.style.display = notify === -1 ? 'none' : 'grid'
      notifyEl.querySelector('use').setAttribute('href', notify === 1 ? '#sg-new/bell' : '#sg-new/bell-cross')
    }
  }

  clearStatus = () => {
    if (!this.props.element) {
      return
    }
    const icon = this.props.element.querySelector('[data-game-watch-icon]')
    const text = this.props.element.querySelector('[data-game-watch-text]')
    if (text) {
      text.innerHTML = 'Мой статус'
    }
    if (icon) {
      icon.outerHTML = renderToString(<div data-game-watch-icon={true}>
        <SVG i="sg-new/plus" adaptive/>
      </div>)
    }
    if (this.props.buttonClasses) {
      this.props.element.classList.remove(...Object.values(this.props.buttonClasses))
      this.props.element.classList.add(this.props.buttonClasses.blank)
    }
    const notifyEl = this.props.element.parentElement.querySelector<HTMLButtonElement>('[data-toggle-game-notify]')
    if (notifyEl) {
      notifyEl.style.display = 'none'
    }
  }

  showAddToCompilaitonModal = () => {
    showModal(GameAddToCompilation, {
      gameId: parseInt(this.props.gameId)
    })
  }

  toggleFavourite = () => {
    const favourite = this.state.favourite
    const errorMessage = !favourite ? 'Не удалось добавить игру в избранное' : 'Не удалось удалить игру из избранного'

    this.setState({
      favourite: !favourite
    })

    Api.post({
      url: `games/favourite`,
      body: {
        gameId: this.props.gameId
      }
    })
      .then(result => {
        if (result.success) {
          showToast({
            type: 'success',
            text: result.message
          })
        } else {
          throw new Error(result.message)
        }
      })
      .catch((e) => {
        captureFetchException(e)
        showToast({
          type: 'error',
          text: errorMessage
        })
        this.setState({
          favourite
        })
      })
  }

  render() {
    const { gameName, status, loaded, gameIsReleased, releaseDate, notify, favourite } = this.state
    const { gameId } = this.props

    if (!loaded) {
      return ''
    }

    const buttons = [];
    [1, 0, 2, 3].forEach((statusId) => {
      const classes = [styles[`status-svg`]]
      const buttonClasses = [styles['button'], styles[`button--${statusId}`]]
      if (statusId === status) {
        buttonClasses.push(styles['button--active'])
      }
      buttons.push(<button
        type="button"
        className={buttonClasses.join(' ')}
        disabled={!gameIsReleased && statusId > 0}
        onClick={() => this.watchGame(statusId)}
      >
        <SVG
          i={`game-status/colorable/${this.statuses[statusId].svg}`}
          class={classes.join(' ')}
        /> {this.statuses[statusId].text}
      </button>)
    })

    const emptyStars = []
    const fillStars = []
    const starsHeaderClasses = [styles['header']]
    const starsClasses = [styles['user-rating__stars']]
    const writeReviewClasses = []
    if (!gameIsReleased) {
      starsHeaderClasses.push(styles['header--disabled'])
      starsClasses.push(styles['user-rating__stars--disabled'])
      writeReviewClasses.push(buttonsCss['outline'])
    } else {
      writeReviewClasses.push(buttonsCss['gray'])
    }
    const ratingColors = {
      1: '--brand-red',
      2: '--brand-red',
      3: '--brand-red',
      4: '--brand-red',
      5: '--yellow-rating',
      6: '--yellow-rating',
      7: '--green',
      8: '--green',
      9: '--green',
      10: '--green',
    }

    for (let i = 1; i <= 5; i++) {
      emptyStars.push(<SVG i='fa/star-empty'/>)
    }

    for (let i = 1; i <= 10; i++) {
      const stars = [];

      for (let j = 1; j <= i / 2; j++) {
        stars.push(<SVG i="fa/star"/>)
      }

      if (i % 2 === 1) {
        stars.push(<SVG i="fa/star-half" className={styles['half-star']}/>)
      }

      fillStars.push(<>
        <input
          type="radio"
          id={`dialog-user-rating-${gameId}-${i}`}
          value={i}
          checked={this.state.rating === i}
          disabled={!gameIsReleased}
        />
        <label
          htmlFor={`dialog-user-rating-${gameId}-${i}`}
          style={`--z-index: ${10 - i}; color: var(${ratingColors[i]})`}
          onClick={() => this.setRating(i)}
          data-disabled={!gameIsReleased}
        >
          {stars}
        </label>
      </>)
    }

    return <Modal onClose={this.props.onClose} showClose>
      <button
        type='button'
        className={styles['notify']}
        style={{
          display: notify === -1 ? 'none' : 'block'
        }}
        data-plugins='toggle-game-notify'
        data-toggle-game-notify={this.props.gameId}
      >
        <SVG i={notify === 1 ? 'sg-new/bell' : 'sg-new/bell-cross'} adaptive/>
      </button>
      <div className={styles['container']}>
        <div className={styles['title']}>{gameName}</div>
        <h2 className={styles['header']}>Мой статус</h2>
        <div className={styles['buttons']}>
          {buttons}
        </div>
        <h2 className={starsHeaderClasses.join(' ')}>Моя оценка</h2>
        <div className={starsClasses.join(' ')}>
          {emptyStars}
          <div className={styles['user-rating__stars__fill']}>
            {fillStars}
          </div>
          <button
            type='button'
            className={styles['user-rating__clear']}
            onClick={() => this.setRating(0)}
            disabled={!gameIsReleased}
          >
            <SVG i='sg-new/cross' adaptive/>
          </button>
          <button type='button' className={cn({
            [styles['user-rating__heart']]: true,
            [styles['user-rating__heart--active']]: favourite
          })} onClick={this.toggleFavourite}>
            <SVG i='v9/heart'/>
          </button>
        </div>
        {!gameIsReleased && <div className={styles['release-date']}>
          {releaseDate}
        </div>}
        <button
          type='button'
          className={writeReviewClasses.join(' ')}
          onClick={this.showReviewForm}
          style='width: 222px'
        >
          <SVG i='sg-new/user-edit' adaptive/> Написать отзыв
        </button>
        <button
          type='button'
          className={buttonsCss['gray']}
          style='width: 222px'
          onClick={this.showAddToCompilaitonModal}
        >
          <SVG i='sg-new/plus' adaptive/> Добавить в подборку
        </button>
      </div>
    </Modal>
  }
}
